import moment from 'moment'
import { retrieveLanguage } from './languageStore'
import { enGB, fi } from 'date-fns/locale'
import { type Locale } from 'date-fns'
import { type AlarmType } from '../redux/alarm-settings/alarm-types'
import { type DeviceAlarmSettings } from '../api/device-api-types'

/**
 * Checks if alarm is enabled in device's alarm settings.
 *
 * @param settings alarm settings for a device
 * @param alarmType type of alarm to check
 * @returns true if alarm is enabled, false otherwise.
 */
export function isAlarmEnabled(settings: DeviceAlarmSettings | null, alarmType: AlarmType): boolean {
  if (settings === null || settings === undefined) return false
  if (alarmType.enabledKey === undefined) return false
  return settings[alarmType.enabledKey] ?? false
}

/**
 * Creates default alarm settings for a device where all alarms are disabled.
 *
 * @returns default alarm settings for the device where all alarms are disabled
 * and maxFlowPerHour is null.
 */
export function createDefaultAlarmSettings(): DeviceAlarmSettings {
  return {
    tooBigFlowEnabled: false,
    backwardFlowEnabled: false,
    magneticFraudEnabled: false,
    opticalFraudEnabled: false,
    moduleRemovedEnabled: false,
    batteryWarningEnabled: false,
    leakageEnabled: false,
    hardwareErrorEnabled: false,
    switchErrorEnabled: false,
    resetDetectedEnabled: false,
    fraudDetectedEnabled: false,
    freezeDetectedEnabled: false,
    emptyTubeDetectedEnabled: false,
    flowLimitEnabled: false,
    maxFlowPerHour: undefined
  }
}

/**
 * Groups an array with type T by type G
 * @param arr array to group
 * @param getter function to group item of type T by
 * @returns array grouped by getter function result
 */
export function groupBy<T, G>(arr: T[], getter: (item: T) => G): Map<G, T[]> {
  const map = new Map<G, T[]>()
  arr.forEach((item) => {
    const key = getter(item)
    const collection = map.get(key)
    if (collection === undefined || collection === null) {
      map.set(key, [item])
    } else {
      collection.push(item)
    }
  })
  return map
}

export function parseCubicMeters(from: number | undefined): string {
  if (from == null) return '0.00 m\u00B3'
  return `${(from / 1000.0).toFixed(2)} m\u00B3`
}

export const ON_TIME = 'ON_TIME'
export const OUT_OF_DATE = 'OUT_OF_DATE'
export const NOT_UPDATED = 'NOT_UPDATED'

export function compareTime(toBeCompared: moment.MomentInput): 'NOT_UPDATED' | 'OUT_OF_DATE' | 'ON_TIME' {
  if (moment().diff(toBeCompared) > 172800000) {
    return NOT_UPDATED
  } else if (moment().diff(toBeCompared) > 86400000) {
    return OUT_OF_DATE
  }
  return ON_TIME
}

export function dateToLocaleOrDefault(date: Date | undefined | null): string {
  const dateToConvert = date ?? new Date()
  return dateToConvert.toLocaleDateString('fi-FI')
}

export function dateToLocaleOrEmpty(date: Date | undefined | null): string {
  if (date == null) {
    return ''
  }

  return date.toLocaleDateString('fi-FI')
}

export function generateExportCsvFileName(startDate: Date, endDate: Date): string {
  const startMonth = (startDate.getMonth() + 1).toString().padStart(2, '0')
  const downloadStartDate = startDate.getDate().toString().padStart(2, '0')
  const endMonth = (endDate.getMonth() + 1).toString().padStart(2, '0')
  const downloadEndDate = endDate.getDate().toString().padStart(2, '0')
  return `export${startDate.getFullYear()}${startMonth}${downloadStartDate}-${endDate.getFullYear()}${endMonth}${downloadEndDate}.csv`
}

export function getLanguageAsDateFnsLocaleObject(): Locale {
  const lan = retrieveLanguage()
  switch (lan) {
    case 'fi': {
      return fi
    }
    case 'en': {
      return enGB
    }
    default: {
      return fi
    }
  }
}

export function formatEpochSecondsToTimestamp(epochSeconds: number): string {
  return moment.unix(epochSeconds).format('DD.MM.YYYY HH:mm:ss')
}

export function isTimestampOverDayOld(unixTimestamp: number | undefined): boolean {
  if (unixTimestamp === undefined) {
    return true
  }
  const timestampMoment = moment.unix(unixTimestamp)
  const fullDaysSinceTimestamp = moment().diff(timestampMoment, 'days')
  return fullDaysSinceTimestamp >= 1.0
}

/**
 * Paginates a list
 * @param list List to paginate
 * @param page current page number
 * @param pageSize page size
 * @returns paginated list
 */
export function paginateList<T>(list: T[], page: number, pageSize: number): T[] {
  const startIndex = ((page - 1) * pageSize)
  return list.slice(startIndex, startIndex + pageSize)
}
